import React, { useState } from 'react';
import {
  Button,
  DialogField,
  DialogFieldError,
  DialogFieldLabel,
  DialogFooter,
  DialogSub,
  Modal,
  TextField,
  TEXT_FIELD_APPEARANCE_PILL,
  TEXT_FIELD_SIZE_LG,
} from '@aibuild/ui';
import { suggestCopyName } from './suggestCopyName';

export interface ProcessDuplicateDialogProps {
  open: boolean;
  onClose: () => void;
  /** The name of the process being duplicated — shown in the body copy. */
  currentName: string;
  /**
   * Names already in use across the user's processes — used to surface the
   * `is-error` state when the chosen name collides. May be omitted when the
   * caller doesn't have a list cache, in which case clientside collision
   * detection is skipped (server-side validation still applies).
   */
  existingNames?: readonly string[];
  /** Suggested initial name — defaults to `${currentName} (copy)`. */
  suggestedName?: string;
  /** Called with the trimmed new name when the user clicks Duplicate. */
  onSubmit: (newName: string) => void | Promise<void>;
  isDuplicating?: boolean;
  dataTestId?: string;
}

const ProcessDuplicateDialog: React.FC<ProcessDuplicateDialogProps> = ({
  open,
  onClose,
  currentName,
  existingNames = [],
  suggestedName,
  onSubmit,
  isDuplicating = false,
  dataTestId = 'process-duplicate-dialog',
}) => {
  const [name, setName] = useState('');
  // Render-time-derived seeding (React 19 pattern): when the dialog
  // transitions from closed -> open, refresh the input with a freshly
  // suggested name. Implemented in render (not useEffect) to satisfy
  // `react-hooks/set-state-in-effect` and to avoid an extra mount+effect
  // round-trip.
  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setName(suggestedName ?? suggestCopyName(currentName, existingNames));
    }
  }

  const trimmed = name.trim();
  const lowered = trimmed.toLowerCase();
  const isTaken =
    !!trimmed &&
    existingNames.some((n) => n.trim().toLowerCase() === lowered);
  const canSave = !!trimmed && !isTaken && !isDuplicating;

  const commit = () => {
    if (!canSave) return;
    // Fire-and-forget: the parent surfaces success / failure through
    // `isDuplicating` and the snackbar pipeline. `Promise.resolve` normalises
    // the `void | Promise<void>` return type so we can chain `.catch` safely
    // when the consumer hands us a sync function (no rejection to swallow,
    // but we still need to discard any returned promise without using
    // `void` as an operator).
    Promise.resolve(onSubmit(trimmed)).catch(() => undefined);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    }
  };

  return (
    <Modal
      open={open}
      onClose={isDuplicating ? () => undefined : onClose}
      title="Duplicate Process"
      size="sm"
      dataTestId={dataTestId}
      hideCloseButton={isDuplicating}
      disableEscape={isDuplicating}
      disableBackdropClose={isDuplicating}
      footer={
        <DialogFooter>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isDuplicating}
            dataTestId={`${dataTestId}__cancel`}
          >
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={commit}
            disabled={!canSave}
            loading={isDuplicating}
            dataTestId={`${dataTestId}__submit`}
          >
            Duplicate
          </Button>
        </DialogFooter>
      }
    >
      <DialogSub data-testid={`${dataTestId}__sub`}>
        Create a copy of <strong>{currentName}</strong> as a new draft. Choose
        a name for the duplicate.
      </DialogSub>
      <DialogField>
        <DialogFieldLabel htmlFor={`${dataTestId}__input`}>
          Name
        </DialogFieldLabel>
        <TextField
          id={`${dataTestId}__input`}
          appearance={TEXT_FIELD_APPEARANCE_PILL}
          size={TEXT_FIELD_SIZE_LG}
          stretch
          selectOnFocus
          autoFocus
          value={name}
          onChange={setName}
          onKeyDown={handleKeyDown}
          disabled={isDuplicating}
          invalid={isTaken}
          dataTestId={`${dataTestId}__field`}
        />
        {isTaken && (
          <DialogFieldError data-testid={`${dataTestId}__error`}>
            Name must be unique
          </DialogFieldError>
        )}
      </DialogField>
    </Modal>
  );
};

export default ProcessDuplicateDialog;
